import { BlockStack, Text, InlineStack, Badge, Icon, Button } from "@shopify/polaris";
import { TooltipDemoOnly as Tooltip } from "./TooltipDemoOnly";
import { CheckIcon } from "@shopify/polaris-icons";
import { STARTER_PLAN, GROWTH_PLAN, PRO_PLAN, ENTERPRISE_PLAN, PLAN_LIMITS, TRIAL_DAYS } from "../constants";

interface PricingTableProps {
  activePlan?: string;
  selectedPlan?: string;
  onSelect: (plan: string) => void;
  isInstallFlow?: boolean;
  submittingPlan?: string | null;
}

export function PricingTable({ activePlan, selectedPlan, onSelect, isInstallFlow, submittingPlan }: PricingTableProps) {
  const plans = [STARTER_PLAN, GROWTH_PLAN, PRO_PLAN, ENTERPRISE_PLAN];
  const currentIndex = activePlan ? plans.indexOf(activePlan) : -1;

  // Users on any paid plan cannot downgrade to Starter (it's a one-time/lifetime entry plan)
  const isOnPaidPlan = activePlan === GROWTH_PLAN || activePlan === PRO_PLAN || activePlan === ENTERPRISE_PLAN;
  const isStarterLocked = isOnPaidPlan && !isInstallFlow;

  const planDisplayNames: Record<string, string> = {
    [STARTER_PLAN]: "Starter",
    [GROWTH_PLAN]: "Growth",
    [PRO_PLAN]: "Pro",
    [ENTERPRISE_PLAN]: "Enterprise",
  };

  const getButtonText = (plan: string) => {
    if (submittingPlan === plan) return "Processing...";

    if (isInstallFlow) {
      return selectedPlan === plan ? "Plan Selected" : "Select Plan";
    }

    // Starter is locked when on a paid plan
    if (plan === STARTER_PLAN && isStarterLocked) return "Not Available";

    const planIndex = plans.indexOf(plan);
    if (plan === activePlan) return "Cancel Subscription";
    if (currentIndex === -1) {
      if (plan === STARTER_PLAN) return "Get Started For Free";
      if (plan === GROWTH_PLAN) return `Start ${TRIAL_DAYS}-day Free Trial`;
      return "Get Started";
    }
    return planIndex > currentIndex ? "Upgrade" : "Downgrade";
  };

  const getCardStyle = (plan: string) => {
    const isSelected = selectedPlan === plan || activePlan === plan;
    const isLocked = plan === STARTER_PLAN && isStarterLocked;

    return {
      backgroundColor: isLocked ? "#fafafa" : "white",
      borderRadius: "24px",
      border: isSelected ? "2px solid #008060" : isLocked ? "2px solid #f1f2f4" : "2px solid #e1e3e5",
      padding: "24px",
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      boxShadow: isSelected
        ? "0 30px 60px rgba(0, 128, 96, 0.15)"
        : isLocked
          ? "none"
          : "0 20px 40px rgba(0, 0, 0, 0.08)",
      transform: isSelected ? "translateY(-8px)" : "none",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      cursor: isInstallFlow ? "pointer" : "default",
      opacity: isLocked ? 0.65 : 1,
    };
  };

  return (
    <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <div style={{ maxWidth: "1280px", width: "100%" }}>
        <style dangerouslySetInnerHTML={{ __html: `
          .pricing-card:hover {
            transform: translateY(-8px) !important;
            box-shadow: 0 20px 35px rgba(0, 0, 0, 0.08) !important;
            border-color: #008060 !important;
          }
          .pricing-cards-row {
            display: flex;
            flex-wrap: wrap;
            gap: 16px;
            justify-content: center;
          }
          @media (max-width: 767px) {
            .pricing-cards-row {
              flex-direction: column;
              align-items: stretch;
            }
            .pricing-card {
              max-width: 100% !important;
            }
          }
        `}} />
        <div className="pricing-cards-row">
          {plans.map((plan) => {
            const config = PLAN_LIMITS[plan];
            const isGrowth = plan === GROWTH_PLAN;
            const isPro = plan === PRO_PLAN;
            const buttonText = getButtonText(plan);

            return (
              <div 
                key={plan} 
                className={`pricing-card${plan === STARTER_PLAN && isStarterLocked ? ' pricing-card--locked' : ''}`}
                onClick={() => isInstallFlow && !isStarterLocked && onSelect(plan)}
                style={{ flex: "1 1 240px", maxWidth: "260px", display: "flex", flexDirection: "column" }}
              >
                <div style={getCardStyle(plan) as any}>
                  <div style={{ flex: 1 }}>
                    <BlockStack gap="200" inlineAlign="start">
                      <div style={{ width: '100%' }}>
                        <InlineStack align="space-between">
                          <Text variant="headingMd" as="h4" fontWeight="bold">{planDisplayNames[plan] || plan}</Text>
                          {plan === STARTER_PLAN && isStarterLocked && <Badge tone="subdued">Entry Plan</Badge>}
                          {plan === STARTER_PLAN && !isStarterLocked && !isOnPaidPlan && activePlan !== STARTER_PLAN && <Badge tone="subdued">Entry Plan</Badge>}
                          {plan === STARTER_PLAN && activePlan === STARTER_PLAN && <Badge tone="success">Current</Badge>}
                          {isGrowth && activePlan !== STARTER_PLAN && <Badge tone="success">Recommended</Badge>}
                          {isPro && !isGrowth && <Badge tone="info">Most Popular</Badge>}
                        </InlineStack>
                      </div>
                      
                      <InlineStack blockAlign="baseline" gap="100" align="start">
                        <Text variant="heading2xl" as="p" alignment="start">${config.price}</Text>
                        {config.price > 0 && <Text variant="bodyMd" as="p" tone="subdued">/ month</Text>}
                      </InlineStack>

                      <BlockStack gap="200" inlineAlign="start">
                        <Text as="p" variant="bodyMd" tone="subdued">
                          {plan === STARTER_PLAN && "Zero upfront. Pay only for the results you get."}
                          {plan === GROWTH_PLAN && "The sweet spot. Low base fee with performance scaling."}
                          {plan === PRO_PLAN && "Maximum protection for high-volume stores."}
                          {plan === ENTERPRISE_PLAN && "Custom high-volume logic and elite account support."}
                        </Text>

                        <div style={{ width: '100%', height: '1px', backgroundColor: '#f1f2f4' }} />

                        <BlockStack gap="100" inlineAlign="start">
                          <InlineStack gap="200" align="start" blockAlign="start" wrap={false}>
                            <div style={{ marginTop: '2px' }}>
                              <Icon source={CheckIcon} tone="success" />
                            </div>
                            <Text variant="bodyMd" as="span">
                              <b>{config.limit === Infinity ? "Unlimited" : config.limit}</b> Automated Recoveries {config.isLifetime ? "(Lifetime)" : "per month"}
                            </Text>
                          </InlineStack>

                          <InlineStack gap="200" align="start" blockAlign="start" wrap={false}>
                            <div style={{ marginTop: '2px' }}>
                              <Icon source={CheckIcon} tone="success" />
                            </div>
                            <Text variant="bodyMd" as="span">
                              <b>{(config.feePercentage * 100).toFixed(plan === ENTERPRISE_PLAN ? 1 : 0)}%</b> Performance Fee
                            </Text>
                          </InlineStack>
                          
                          {currentIndex === -1 && config.price > 0 && (
                            <InlineStack gap="200" align="start" blockAlign="start" wrap={false}>
                              <div style={{ marginTop: '2px' }}>
                                <Icon source={CheckIcon} tone="success" />
                              </div>
                              <Text variant="bodyMd" as="span"><b>{TRIAL_DAYS}-Day</b> Free Trial</Text>
                            </InlineStack>
                          )}

                          <InlineStack gap="200" align="start" blockAlign="start" wrap={false}>
                            <div style={{ marginTop: '2px' }}>
                              <Icon source={CheckIcon} tone="success" />
                            </div>
                            <Text variant="bodyMd" as="span">
                               {plan === STARTER_PLAN && "Basic AI Agent Access"}
                              {plan === GROWTH_PLAN && "Full Confidence Slider"}
                              {plan === PRO_PLAN && "Customizable Logic Tuning"}
                              {plan === ENTERPRISE_PLAN && "Custom Logic Consultation"}
                            </Text>
                          </InlineStack>

                          {plan === GROWTH_PLAN && (
                            <InlineStack gap="200" align="start" blockAlign="start" wrap={false}>
                              <div style={{ marginTop: '2px' }}>
                                <Icon source={CheckIcon} tone="success" />
                              </div>
                              <Text variant="bodyMd" as="span">Basic Email Notifications</Text>
                            </InlineStack>
                          )}

                          {plan === PRO_PLAN && (
                            <InlineStack gap="200" align="start" blockAlign="start" wrap={false}>
                              <div style={{ marginTop: '2px' }}>
                                <Icon source={CheckIcon} tone="success" />
                              </div>
                              <Text variant="bodyMd" as="span">Priority Webhook Processing</Text>
                            </InlineStack>
                          )}

                          <InlineStack gap="200" align="start" blockAlign="start" wrap={false}>
                            <div style={{ marginTop: '2px' }}>
                              <Icon source={CheckIcon} tone="success" />
                            </div>
                            <Text variant="bodyMd" as="span">
                              {plan === STARTER_PLAN && "Community Support"}
                              {plan === GROWTH_PLAN && "Standard Support"}
                              {plan === PRO_PLAN && "Priority Support"}
                              {plan === ENTERPRISE_PLAN && "Dedicated Account Manager"}
                            </Text>
                          </InlineStack>
                          {plan !== STARTER_PLAN && (
                            <InlineStack gap="200" align="start" blockAlign="start" wrap={false}>
                              <div style={{ marginTop: '2px' }}>
                                <Icon source={CheckIcon} tone="success" />
                              </div>
                              <Text variant="bodyMd" as="span">Data Management (CSV/Reset)</Text>
                            </InlineStack>
                          )}


                          <InlineStack gap="200" align="start" blockAlign="start" wrap={false}>
                            <div style={{ marginTop: '2px' }}>
                              <Icon source={CheckIcon} tone="success" />
                            </div>
                            <Text variant="bodyMd" as="span">ROI & Performance Analytics</Text>
                          </InlineStack>

                          <div style={{ width: '100%', height: '1px', backgroundColor: '#f1f2f4', margin: '4px 0' }} />
                          
                          <InlineStack gap="200" align="start" blockAlign="start" wrap={false}>
                            <div style={{ marginTop: '2px' }}>
                              <Icon source={CheckIcon} tone="success" />
                            </div>
                            <Text variant="bodyMd" as="span">
                              <b>${config.usageCap.toLocaleString()}</b> Safety Usage Cap
                            </Text>
                          </InlineStack>

                        </BlockStack>

                        <div style={{ 
                          marginTop: '4px',
                          padding: '8px 12px', 
                          backgroundColor: '#f6ffed', 
                          borderRadius: '8px', 
                          border: '1px solid #b7eb8f',
                          width: '100%'
                        }}>
                          <BlockStack gap="100">
                            <Text variant="bodySm" tone="success" fontWeight="bold" as="span">Expected Recovery Potental:</Text>
                            <Text variant="headingLg" as="p" tone="success">
                              {config.expectedRecovery}
                            </Text>
                          </BlockStack>
                        </div>
                      </BlockStack>
                    </BlockStack>
                  </div>

                  {!isInstallFlow && (
                    <div style={{ marginTop: "auto", paddingTop: "16px" }}>
                      {plan === STARTER_PLAN && isStarterLocked ? (
                        // Starter is locked — show a non-clickable locked state
                        <Tooltip content="Starter is a one-time entry plan and cannot be selected once you're on a paid plan. You can only upgrade from here.">
                          <div style={{
                            width: '100%',
                            padding: '10px 16px',
                            borderRadius: '8px',
                            textAlign: 'center',
                            backgroundColor: '#f1f2f4',
                            color: '#8c9196',
                            fontWeight: '600',
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            cursor: 'not-allowed',
                            border: '1px solid #e1e3e5',
                          }}>
                            <span style={{ fontSize: '16px' }}>🔒</span> Upgrade Only
                          </div>
                        </Tooltip>
                      ) : (
                        <Tooltip content={
                          plan === STARTER_PLAN ? "Start recovering revenue for free. Perfect for small stores." :
                          plan === GROWTH_PLAN ? "The most popular choice! Balance higher limits with powerful AI tuning." :
                          plan === PRO_PLAN ? "For high-volume brands needing maximum insight and priority support." :
                          "Our top-tier service with unlimited capacity and white-glove setup."
                        }>
                          <Button 
                            onClick={() => onSelect(plan)}
                            variant={activePlan === plan ? "secondary" : "primary"}
                            fullWidth
                            tone={activePlan === plan ? "critical" : (isGrowth ? "success" : undefined)}
                            loading={submittingPlan === plan}
                            disabled={!!submittingPlan && submittingPlan !== plan}
                          >
                            {buttonText}
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  )}
                  
                  {isInstallFlow && (
                    <div style={{ marginTop: "auto", paddingTop: "16px" }}>
                      <div style={{ 
                        width: '100%', 
                        padding: '12px', 
                        borderRadius: '8px', 
                        textAlign: 'center',
                        backgroundColor: selectedPlan === plan ? '#008060' : '#f4f6f8',
                        color: selectedPlan === plan ? 'white' : '#111827',
                        fontWeight: '600',
                        fontSize: '14px',
                        transition: 'all 0.2s'
                      }}>
                        {buttonText}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        
        <div style={{ marginTop: '16px', padding: '24px', backgroundColor: '#f9fafb', borderRadius: '16px', border: '1px solid #e1e3e5' }}>
          <BlockStack gap="200">
            <Text variant="headingSm" as="h5">How Billing Works</Text>
            <Text variant="bodySm" tone="subdued" as="p">
              All plans include a monthly subscription fee and a performance-based fee for revenue recovered by the AI.
              Performance fees are billed as <b>Shopify Usage Charges</b> and will appear on your monthly Shopify invoice.
              The <b>Safety Usage Cap</b> is the maximum total performance fee you can be charged in a single billing cycle.
              Free trials apply to new subscriptions only; upgrades and downgrades are processed without a trial period.
            </Text>
            <Text variant="bodySm" tone="subdued" as="p">
              <b>Note on Starter:</b> The Starter plan is a one-time entry plan with a lifetime recovery cap of 10 saves.
              Once you upgrade to a paid plan, you cannot return to Starter — but you can always upgrade, downgrade between paid tiers, or cancel.
            </Text>
          </BlockStack>
        </div>
      </div>
    </div>
  );
}
